import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { X, Loader2, Pipette } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

const QueueSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
  color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
  greetingMessage: Yup.string(),
});

const QueueModal = ({ open, onClose, queueId }) => {
  const initialState = { name: "", color: "", greetingMessage: "" };
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queue, setQueue] = useState(initialState);
  const greetingRef = useRef();

  useEffect(() => {
    (async () => {
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue((prev) => ({ ...prev, ...data }));
      } catch (err) { toastError(err); }
    })();
    return () => setQueue(initialState);
  }, [queueId, open]);

  const handleClose = () => { onClose(); setQueue(initialState); };

  const handleSaveQueue = async (values) => {
    try {
      if (queueId) await api.put(`/queue/${queueId}`, values);
      else await api.post("/queue", values);
      toast.success("Queue saved successfully");
      handleClose();
    } catch (err) { toastError(err); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {queueId ? i18n.t("queueModal.title.edit") : i18n.t("queueModal.title.add")}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Formik
          initialValues={queue}
          enableReinitialize={true}
          validationSchema={QueueSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => { handleSaveQueue(values); actions.setSubmitting(false); }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("queueModal.form.name")}</label>
                  <Field
                    name="name"
                    autoFocus
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                      touched.name && errors.name ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                    }`}
                  />
                  {touched.name && errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("queueModal.form.color")}</label>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 flex-shrink-0" style={{ backgroundColor: values.color || "#ccc" }} />
                    <Field
                      name="color"
                      id="color"
                      onFocus={() => { setColorPickerModalOpen(true); greetingRef.current?.focus(); }}
                      className={`flex-1 px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                        touched.color && errors.color ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                      }`}
                    />
                    <button type="button" onClick={() => setColorPickerModalOpen(true)} className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      <Pipette className="w-4 h-4" />
                    </button>
                  </div>
                  {touched.color && errors.color && <p className="text-xs text-red-500 mt-1">{errors.color}</p>}
                  <ColorPicker
                    open={colorPickerModalOpen}
                    handleClose={() => setColorPickerModalOpen(false)}
                    currentColor={values.color}
                    onChange={(color) => { setFieldValue("color", color); setQueue({ ...values, color }); }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("queueModal.form.greetingMessage")}</label>
                  <Field
                    as="textarea"
                    name="greetingMessage"
                    inputRef={greetingRef}
                    rows={4}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none ${
                      touched.greetingMessage && errors.greetingMessage ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                    }`}
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
                <button type="button" onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  {i18n.t("queueModal.buttons.cancel")}
                </button>
                <button type="submit" disabled={isSubmitting} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {queueId ? i18n.t("queueModal.buttons.okEdit") : i18n.t("queueModal.buttons.okAdd")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default QueueModal;
